import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Fragment, type ReactNode, useRef, useState } from 'react';

import { Spinner } from '@apps/web/components/ui/spinner';
import {
    applyCandidateSelections,
    useOutreachCandidateSnapshot,
} from '@apps/web/features/phoebe_chat_v3/api/recommendations_api';
import { type HarnessScope } from '@apps/web/features/phoebe_chat_v3/model/event_types';
import {
    type ShiftWindow,
    unitHeader,
    type UnitHeaderParts,
} from '@apps/web/features/phoebe_chat_v3/model/recommendation_row_summary';
import { type OrganizationEvent } from '@apps/web/hooks/organization_event_schemas';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { toast } from '@apps/web/lib/app_notifications';
import { organizationEventReferencesOutreach } from '@apps/web/lib/organization_event_resources';
import { cn } from '@apps/web/lib/utils';
import {
    type SnapshotCandidate,
    type SnapshotUnit,
} from '@libraries/typescript/api_client/schema_types';

// One agent turn stages per unit, so an N-unit pass publishes N events.
const SELECTION_EVENT_THROTTLE_MS = 3000;

// Bounded by the selection, never by the candidate pool.
const MAX_VISIBLE_RECIPIENTS = 24;

const MAX_LISTED_WINDOWS = 4;

type Recipient = Readonly<{
    readonly candidate: SnapshotCandidate;
    readonly staged: boolean;
}>;

type CoverageBand = Readonly<{
    readonly label: string | null;
    readonly recipients: ReadonlyArray<Recipient>;
}>;

// Dropping everyone empties the response, and "nobody qualifies" would then
// be a lie: they qualified, the coordinator dropped them.
function hasDroppedFor(
    dropped: ReadonlyMap<string, SnapshotCandidate>,
    unitKey: string | undefined,
): boolean {
    if (unitKey === undefined) {
        return false;
    }
    return [...dropped.keys()].some((key) => key.startsWith(`${unitKey}:`));
}

// A unit is a shift group or a bare shift; the optimistic tick, the write,
// and the pager all key off this one derivation.
function unitKeyOf(unit: SnapshotUnit): string | undefined {
    return unit.group_id ?? unit.shifts[0]?.shift_id;
}

export function RecommendationsCard({
    scope,
    outreachId,
    organizationTimeZone,
}: Readonly<{
    readonly scope: HarnessScope;
    readonly outreachId: string;
    readonly organizationTimeZone: string;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const snapshot = useOutreachCandidateSnapshot(scope, outreachId);
    // Which unit is on screen is ephemeral presentation, not navigation, so
    // it stays local rather than in the URL.
    const [unitIndex, setUnitIndex] = useState(0);
    // The unit the reader chose, so a refetch cannot slide another under it.
    const [pinnedUnitKey, setPinnedUnitKey] = useState<string | undefined>(
        undefined,
    );
    // A tick the server has not confirmed yet, keyed by unit so switching
    // shifts never shows another shift's in-flight change.
    const [optimistic, setOptimistic] = useState<ReadonlyMap<string, boolean>>(
        new Map(),
    );
    // Newest write per key wins; an older one that lands late must not
    // clear the tick a later click already painted.
    const writeTokens = useRef<Map<string, number>>(new Map());
    // Server rows describe the pre-stage world until the refetch lands, so
    // a row staged this session is still the coordinator's to withdraw.
    // Keyed by unit: the same caregiver is a separate decision per unit.
    const locallyStaged = useRef<Set<string>>(new Set());
    // The snapshot returns staged rows only, so a drop takes the caregiver
    // out of the next response entirely. Holding the row itself, not just its
    // id, is what keeps the name on screen and one click from coming back.
    const locallyDropped = useRef<Map<string, SnapshotCandidate>>(new Map());
    const units = snapshot.data?.units ?? [];
    // Candidate rows are swept a week after the outreach ends, but the
    // receipt replays forever; a null stamp means swept, not "nobody".
    const expired =
        snapshot.data !== undefined && snapshot.data.computed_at === null;
    // Follow the unit, not its position: a refetch that drops an earlier
    // unit would otherwise slide a different shift under the reader.
    const rememberedIndex = units.findIndex(
        (candidateUnit) => unitKeyOf(candidateUnit) === pinnedUnitKey,
    );
    const activeIndex =
        rememberedIndex >= 0
            ? rememberedIndex
            : Math.min(unitIndex, Math.max(units.length - 1, 0));
    const unit = units[activeIndex];

    // Staging and dismissing publish the suggestion pair, not
    // `outreach_updated`: the outreach row itself is untouched, so only a
    // candidate rebuild carries that one. Listening to all three keeps this
    // card level with the outreach page while candidates are being built.
    // Filter before the throttle: throttling replays only the latest
    // suppressed event, so another outreach's would drop this one's refresh.
    const throttledRepaint = useThrottledCallback(() => {
        void snapshot.mutate();
    }, SELECTION_EVENT_THROTTLE_MS);
    function repaintOnOutreachEvent(event: OrganizationEvent): void {
        if (organizationEventReferencesOutreach(event, outreachId)) {
            throttledRepaint();
        }
    }
    const listenerOptions = { organizationId: scope.organizationId };
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        repaintOnOutreachEvent,
        listenerOptions,
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_removed',
        repaintOnOutreachEvent,
        listenerOptions,
    );
    useListenForOrganizationEvent(
        'outreach_updated',
        repaintOnOutreachEvent,
        listenerOptions,
    );

    function step(delta: number): void {
        const next = activeIndex + delta;
        const target = units[next];
        if (target === undefined) {
            return;
        }
        setUnitIndex(next);
        setPinnedUnitKey(unitKeyOf(target));
    }

    // The name owns no durable state: it writes the selection row the
    // outreach page and the agent already read. It strikes through at once so
    // the click feels immediate, and the refetch is what makes it true.
    function toggleCandidate(
        candidate: SnapshotCandidate,
        staged: boolean,
    ): void {
        const caregiverId = candidate.caregiver_id;
        const unitId = unit === undefined ? undefined : unitKeyOf(unit);
        if (unit === undefined || unitId === undefined) {
            return;
        }
        const key = `${unitId}:${caregiverId}`;
        // Rapid toggles race. Stamp each write and let only the newest one
        // for a key clear or roll back, so a slow first click cannot erase
        // the tick a later click already painted.
        const token = (writeTokens.current.get(key) ?? 0) + 1;
        writeTokens.current.set(key, token);
        if (staged) {
            locallyStaged.current.add(key);
            locallyDropped.current.delete(key);
        } else {
            locallyDropped.current.set(key, candidate);
        }
        setOptimistic((current) => new Map(current).set(key, staged));
        void applyCandidateSelections(scope, {
            outreachId,
            unitType: unit.group_id === null ? 'shift' : 'shift_group',
            unitId,
            staged: new Map([[caregiverId, staged]]),
            stagedByUser: new Set([
                ...unit.candidates
                    .filter(
                        (row) =>
                            row.selection_status === 'staged' &&
                            row.selection_staged_by === 'user',
                    )
                    .map((row) => row.caregiver_id),
                ...unit.candidates
                    .map((row) => row.caregiver_id)
                    .filter((id) =>
                        locallyStaged.current.has(`${unitId}:${id}`),
                    ),
            ]),
        })
            .then((rejected) => {
                const superseded = writeTokens.current.get(key) !== token;
                if (rejected.has(caregiverId)) {
                    if (staged) {
                        locallyStaged.current.delete(key);
                        locallyDropped.current.set(key, candidate);
                    }
                    if (!superseded) {
                        toast.error(
                            'Could not update the caregiver selection.',
                            { persistNotification: false },
                        );
                    }
                }
                return superseded ? undefined : snapshot.mutate();
            })
            .catch(() => {
                if (staged) {
                    locallyStaged.current.delete(key);
                    // A restore that failed must leave the name where it was,
                    // struck through: dropping it from the list too would put
                    // it out of reach of the only control that brings it back.
                    locallyDropped.current.set(key, candidate);
                }
                if (writeTokens.current.get(key) === token) {
                    toast.error('Could not update the caregiver selection.', {
                        persistNotification: false,
                    });
                }
            })
            // Drop the override either way: on success the refetched row now
            // carries it, on failure the row is the truth we should show.
            .finally(() => {
                if (writeTokens.current.get(key) !== token) {
                    return;
                }
                setOptimistic((current) => {
                    const next = new Map(current);
                    next.delete(key);
                    return next;
                });
            });
    }

    if (snapshot.isLoading) {
        return (
            <CardShell>
                <div className="text-v3-chat flex items-center justify-center gap-2 px-3 py-6">
                    <Spinner className="size-4" />
                    <span className="text-muted-foreground">
                        Loading caregivers…
                    </span>
                </div>
            </CardShell>
        );
    }
    if (snapshot.error !== undefined) {
        return (
            <CardShell>
                <p className="text-muted-foreground text-v3-chat px-3 py-6 text-center">
                    Couldn’t load the caregivers for this outreach.
                </p>
            </CardShell>
        );
    }
    if (unit === undefined) {
        return (
            <CardShell>
                <p className="text-muted-foreground text-v3-chat px-3 py-6 text-center">
                    No shifts on this outreach yet.
                </p>
            </CardShell>
        );
    }

    return (
        <CardShell>
            <UnitHeader
                header={unitHeader(unit, organizationTimeZone, formatName)}
                unitNoun={unit.group_id === null ? 'Shift' : 'Group'}
                index={activeIndex}
                total={units.length}
                onPrevious={() => step(-1)}
                onNext={() => step(1)}
            />
            {unit.candidates.length === 0 &&
            !hasDroppedFor(locallyDropped.current, unitKeyOf(unit)) ? (
                <p className="text-muted-foreground text-v3-chat px-3 py-6 text-center">
                    {expired
                        ? 'This shortlist is no longer kept. Open the outreach to rebuild it.'
                        : 'Nobody qualifies for this shift yet.'}
                </p>
            ) : (
                <Recipients
                    // A different list, not a longer one: reset on paging.
                    key={unitKeyOf(unit)}
                    unit={unit}
                    optimistic={optimistic}
                    locallyDropped={locallyDropped.current}
                    onToggle={toggleCandidate}
                />
            )}
        </CardShell>
    );
}

function CardShell({
    children,
}: Readonly<{ readonly children: ReactNode }>): ReactNode {
    return (
        <section
            aria-label="Recommended caregivers"
            className="bg-card min-w-0 overflow-hidden rounded-[10px] border shadow-[0_1px_3px_rgba(0,0,0,0.07)]"
        >
            {children}
        </section>
    );
}

function UnitHeader({
    header,
    unitNoun,
    index,
    total,
    onPrevious,
    onNext,
}: Readonly<{
    readonly header: UnitHeaderParts;
    readonly unitNoun: string;
    readonly index: number;
    readonly total: number;
    readonly onPrevious: () => void;
    readonly onNext: () => void;
}>): ReactNode {
    return (
        <header className="flex min-w-0 items-start gap-3 border-b px-3 py-2.5">
            <div className="min-w-0 flex-1">
                {/* Paging swaps the whole list, so announce the new unit. */}
                <p
                    aria-live="polite"
                    className="text-v3-chat truncate font-semibold"
                >
                    {header.title}
                </p>
                <div className="text-muted-foreground text-v3-chat-meta">
                    {header.countAndRule === '' ? null : (
                        <p>{header.countAndRule}</p>
                    )}
                    {header.perShiftTimes.length > 0 ? (
                        <ShiftWindowGrid lines={header.perShiftTimes} />
                    ) : (
                        <UnitWhen header={header} />
                    )}
                </div>
            </div>
            {total < 2 ? null : (
                <nav
                    aria-label="Shifts on this outreach"
                    className="flex shrink-0 items-center gap-0.5"
                >
                    <span className="text-muted-foreground text-v3-chat-meta mr-1 whitespace-nowrap tabular-nums">
                        {unitNoun} {index + 1} of {total}
                    </span>
                    <StepButton
                        label="Previous shift"
                        disabled={index === 0}
                        onClick={onPrevious}
                    >
                        <ChevronLeft aria-hidden={true} className="size-4" />
                    </StepButton>
                    <StepButton
                        label="Next shift"
                        disabled={index >= total - 1}
                        onClick={onNext}
                    >
                        <ChevronRight aria-hidden={true} className="size-4" />
                    </StepButton>
                </nav>
            )}
        </header>
    );
}

function UnitWhen({
    header,
}: Readonly<{ readonly header: UnitHeaderParts }>): ReactNode {
    const separator = header.daysAreSpan ? ' – ' : ' · ';
    const inlineWindow = header.shiftCount === 1 && header.sharedTime !== null;
    return (
        <Fragment>
            <p>
                {header.days.map((day, at) => (
                    // A date that breaks after its month reads as "Sat, Sep"
                    // over "26".
                    <Fragment key={`${at}-${day}`}>
                        {at === 0 ? null : separator}
                        <span className="whitespace-nowrap">{day}</span>
                    </Fragment>
                ))}
                {inlineWindow ? (
                    <Fragment>
                        {' · '}
                        <span className="whitespace-nowrap tabular-nums">
                            {header.sharedTime}
                        </span>
                    </Fragment>
                ) : null}
            </p>
            {header.sharedTime !== null && !inlineWindow ? (
                <p className="whitespace-nowrap tabular-nums">
                    {header.sharedTime} each
                </p>
            ) : null}
        </Fragment>
    );
}

function ShiftWindowGrid({
    lines,
}: Readonly<{ readonly lines: ReadonlyArray<ShiftWindow> }>): ReactNode {
    const listed = lines.slice(0, MAX_LISTED_WINDOWS);
    const hidden = lines.length - listed.length;
    return (
        <div className="grid w-fit grid-cols-[auto_auto] gap-x-3.5 tabular-nums">
            {listed.map((line, at) => (
                <Fragment key={`${at}-${line.day}`}>
                    <span className="whitespace-nowrap">{line.day}</span>
                    <span className="text-right whitespace-nowrap">
                        {line.window}
                    </span>
                </Fragment>
            ))}
            {hidden > 0 ? <span>+{hidden} more</span> : null}
        </div>
    );
}

function StepButton({
    label,
    disabled,
    onClick,
    children,
}: Readonly<{
    readonly label: string;
    readonly disabled: boolean;
    readonly onClick: () => void;
    readonly children: ReactNode;
}>): ReactNode {
    return (
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 flex size-6 shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-[3px] disabled:opacity-30"
        >
            {children}
        </button>
    );
}

function Recipients({
    unit,
    optimistic,
    locallyDropped,
    onToggle,
}: Readonly<{
    readonly unit: SnapshotUnit;
    readonly optimistic: ReadonlyMap<string, boolean>;
    readonly locallyDropped: ReadonlyMap<string, SnapshotCandidate>;
    readonly onToggle: (candidate: SnapshotCandidate, staged: boolean) => void;
}>): ReactNode {
    const [expanded, setExpanded] = useState(false);
    const unitKey = unitKeyOf(unit);
    const present = new Set(
        unit.candidates.map((candidate) => candidate.caregiver_id),
    );
    const dropped = [...locallyDropped]
        .filter(
            ([key, candidate]) =>
                key.startsWith(`${unitKey}:`) &&
                !present.has(candidate.caregiver_id),
        )
        .map(([, candidate]) => candidate);
    const recipients = [...unit.candidates, ...dropped]
        .map((candidate) => {
            const key = `${unitKey}:${candidate.caregiver_id}`;
            return {
                candidate,
                // A cached dropped row still says staged; only a row the
                // server returned can be, e.g. one restored on the outreach page.
                staged:
                    optimistic.get(key) ??
                    (present.has(candidate.caregiver_id) &&
                        candidate.selection_status === 'staged'),
            };
        })
        .filter(
            (row) =>
                row.staged ||
                locallyDropped.has(`${unitKey}:${row.candidate.caregiver_id}`),
        );
    const keptCount = recipients.filter((row) => row.staged).length;
    const limit = expanded ? recipients.length : MAX_VISIBLE_RECIPIENTS;
    const hidden = Math.max(recipients.length - limit, 0);
    const bands = visibleBands(
        coverageBands(recipients, unit.shifts.length),
        limit,
    );
    return (
        <div className="px-3 pt-2.5 pb-3">
            {keptCount === 0 ? (
                <p className="text-muted-foreground text-v3-chat mb-2">
                    Nobody selected. Phoebe won’t text anyone about this.
                </p>
            ) : (
                <p className="text-v3-chat mb-2">
                    Texting{' '}
                    <span className="font-semibold">
                        {keptCount}{' '}
                        {keptCount === 1 ? 'caregiver' : 'caregivers'}
                    </span>
                </p>
            )}
            <div className="space-y-2">
                {bands.map((band, at) => (
                    <div key={band.label ?? 'everyone'}>
                        {band.label === null ? null : (
                            <p className="text-muted-foreground text-v3-chat-meta mb-0.5">
                                {band.label}
                            </p>
                        )}
                        <div className="-mx-1.5 flex flex-wrap gap-x-1.5 gap-y-0.5">
                            {band.recipients.map((row) => (
                                <RecipientName
                                    key={row.candidate.caregiver_id}
                                    candidate={row.candidate}
                                    staged={row.staged}
                                    onToggle={onToggle}
                                />
                            ))}
                            {hidden > 0 && at === bands.length - 1 ? (
                                <button
                                    type="button"
                                    onClick={() => setExpanded(true)}
                                    className="text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-ring/50 text-v3-chat-meta rounded-md px-1.5 font-medium outline-none focus-visible:ring-[3px]"
                                >
                                    +{hidden} more
                                </button>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function RecipientName({
    candidate,
    staged,
    onToggle,
}: Readonly<{
    readonly candidate: SnapshotCandidate;
    readonly staged: boolean;
    readonly onToggle: (candidate: SnapshotCandidate, staged: boolean) => void;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    return (
        <button
            type="button"
            aria-pressed={staged}
            aria-label={`${staged ? 'Do not text' : 'Text'} ${formatName(candidate.caregiver_name)}`}
            onClick={() => onToggle(candidate, !staged)}
            className={cn(
                'text-v3-chat hover:bg-muted focus-visible:ring-ring/50 group flex max-w-full items-center gap-1 rounded-md px-1.5 font-medium outline-none focus-visible:ring-[3px]',
                !staged && 'text-muted-foreground font-normal',
            )}
        >
            <span className={cn('truncate', !staged && 'line-through')}>
                {formatName(candidate.caregiver_name)}
            </span>
            {/* At rest a name is just a name; the affordance arrives on hover
                so a list of people does not read as a list of controls. */}
            <X
                aria-hidden={true}
                className={cn(
                    'h-3 w-0 shrink-0 opacity-0 group-hover:w-3 group-hover:opacity-100',
                    !staged && 'w-3 rotate-45 opacity-100',
                )}
            />
        </button>
    );
}

/** Names grouped by how much of the unit each one can cover. */
function coverageBands(
    recipients: ReadonlyArray<Recipient>,
    shiftCount: number,
): ReadonlyArray<CoverageBand> {
    const levels = [
        ...new Set(recipients.map((row) => row.candidate.covered_shift_count)),
    ].sort((left, right) => right - left);
    // One shift has nothing to cover partially, and one band states nothing
    // the names do not already carry.
    if (shiftCount < 2 || levels.length < 2) {
        return [{ label: null, recipients }];
    }
    return levels.map((level) => ({
        label:
            level >= shiftCount
                ? `Covers all ${shiftCount}`
                : `Covers ${level} of ${shiftCount}`,
        recipients: recipients.filter(
            (row) => row.candidate.covered_shift_count === level,
        ),
    }));
}

// The cap counts across bands in order, so the overflow lands wherever the
// names run out rather than trimming each band.
function visibleBands(
    bands: ReadonlyArray<CoverageBand>,
    limit: number,
): ReadonlyArray<CoverageBand> {
    const visible: Array<CoverageBand> = [];
    let taken = 0;
    for (const band of bands) {
        const slice = band.recipients.slice(0, Math.max(limit - taken, 0));
        taken += slice.length;
        if (slice.length > 0) {
            visible.push({ label: band.label, recipients: slice });
        }
    }
    return visible;
}
